/**
 * New building: one dialog used from the Buildings page and the sidebar's Buildings "+" (backlog #58).
 * Needs buildings.manage (the API enforces it). On save the building is followed, so it shows under Buildings in the
 * sidebar, and `onCreated` gets it (the sidebar opens its page).
 */
import { useMemo, useState } from 'react';
import type { Location } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Button, Field, Input, Modal, Select, Textarea } from '@/components/ui';
import { useBuildingTypes } from './common';

const blank = { name: '', kind: '', code: '', address: '', timezone: 'Europe/London', description: '' };

/** UK and nearby first; every other IANA zone after (browsers that list them). */
export function timezones(): string[] {
  const first = ['Europe/London', 'Europe/Dublin', 'Europe/Paris', 'Europe/Amsterdam', 'Europe/Berlin', 'Europe/Madrid'];
  let all: string[] = [];
  try { all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? []; } catch { all = []; }
  return [...first, ...all.filter(z => !first.includes(z))];
}

export function NewBuildingModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated?: (b: Location) => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const types = useBuildingTypes();
  const zones = useMemo(timezones, []);
  const [n, setN] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The picker only lists active types. The chosen type falls back to the first of them, so a retired type (say Site)
  // is never sent while the select shows something else. Before this, the hidden default 'site' was posted.
  const active = (types.data?.types ?? []).filter(t => !t.retired_at);
  const kind = active.some(t => t.key === n.kind) ? n.kind : active[0]?.key ?? '';
  const close = () => { setN(blank); setError(null); onClose(); };
  const save = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!ws || !n.name.trim() || !kind || busy) return;
    setBusy(true); setError(null);
    try {
      const b = await api.createLocation(ws, { name: n.name.trim(), kind, code: n.code.trim() || undefined, address: n.address.trim() || undefined, timezone: n.timezone, description: n.description.trim() || undefined });
      // Follow it so it shows under Buildings in the sidebar straight away.
      await api.followBuilding(b.id).catch(() => undefined);
      await Promise.all([['buildings', ws], ['locations', ws], ['wf', ws]].map(queryKey => queryClient.invalidateQueries({ queryKey })));
      toast(`${b.name} added`, 'success');
      setN(blank);
      onClose();
      onCreated?.(b);
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };
  const legend = 'mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500';
  return (
    <Modal open={open} onClose={close} title="New building"
      footer={<><Button variant="secondary" onClick={close}>Cancel</Button><Button onClick={() => void save()} disabled={!n.name.trim() || !kind || busy} data-testid="new-building-save">{busy ? 'Adding…' : 'Add building'}</Button></>}>
      <form onSubmit={e => void save(e)} className="space-y-5" data-testid="new-building-form">
        <fieldset>
          <legend className={legend}>About the building</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name" className="sm:col-span-2"><Input autoFocus value={n.name} maxLength={120} placeholder="e.g. Wrexham Store" onChange={e => setN({ ...n, name: e.target.value })} /></Field>
            <Field label="Type"><Select value={kind} onChange={e => setN({ ...n, kind: e.target.value })} data-testid="new-building-type">{active.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}</Select></Field>
            <Field label="Short code" hint="Optional, up to 20 characters, e.g. WRX"><Input value={n.code} maxLength={20} onChange={e => setN({ ...n, code: e.target.value })} /></Field>
          </div>
        </fieldset>
        <fieldset>
          <legend className={legend}>Where it is</legend>
          <div className="grid gap-3">
            <Field label="Address" hint="Optional"><Textarea rows={2} value={n.address} maxLength={500} placeholder="e.g. Unit 4, Plas Coch Retail Park, Wrexham LL11 2BW" onChange={e => setN({ ...n, address: e.target.value })} /></Field>
            <Field label="Time zone" hint="Shift and clock-in times for this building use it"><Select value={n.timezone} onChange={e => setN({ ...n, timezone: e.target.value })}>{zones.map(z => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}</Select></Field>
          </div>
        </fieldset>
        <fieldset>
          <legend className={legend}>Description</legend>
          <Field label="What happens here" hint="Optional"><Textarea rows={2} value={n.description} maxLength={2000} onChange={e => setN({ ...n, description: e.target.value })} /></Field>
        </fieldset>
        {error && <p role="alert" className="text-sm text-rose-600">{error}</p>}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
